import { browserStorage } from '../../audio/settings'
import type { AudioStorage } from '../../audio/settings'
import { Store } from '../../net/store'

/**
 * The Reduce effects setting (PHASE3 T3.7), kept in localStorage `runkr.fx`: `'off'` = reduced
 * (no screen shake, no confetti, fewer particles); anything else, or nothing, = full effects.
 * The settings screen writes it; the race stage reads it once per build (`readFxLevel`).
 */
export const FX_KEY = 'runkr.fx'

/** What the race and the podium draw at the current setting. */
export interface FxLevel {
  /** Full effects (the setting is on). */
  effects: boolean
  /** Particles in a crate pickup burst. */
  crateParticles: number
  /** The rising sparkle where a shard was taken. */
  shardSparkle: boolean
  /** The winner's confetti on the podium. */
  confetti: boolean
  /** Camera shake (none exists yet; whoever adds one reads this). */
  screenShake: boolean
}

const FULL: Readonly<FxLevel> = {
  effects: true,
  crateParticles: 6,
  shardSparkle: true,
  confetti: true,
  screenShake: true,
}

const REDUCED: Readonly<FxLevel> = {
  effects: false,
  crateParticles: 3,
  shardSparkle: false,
  confetti: false,
  screenShake: false,
}

export function fxLevel(effects: boolean): FxLevel {
  return { ...(effects ? FULL : REDUCED) }
}

/** The stored flag: only `'off'` turns effects down. */
export function parseFx(raw: string | null | undefined): boolean {
  return raw !== 'off'
}

function readRaw(storage: AudioStorage): string | null {
  try {
    return storage?.getItem(FX_KEY) ?? null
  } catch {
    return null
  }
}

/** One read of the setting, for a stage build (a change applies from the next race). */
export function readFxLevel(storage: AudioStorage = browserStorage()): FxLevel {
  return fxLevel(parseFx(readRaw(storage)))
}

export interface FxSettingsStore {
  /** true = full effects. */
  readonly store: Store<boolean>
  set(effects: boolean): void
}

export function createFxSettings(storage: AudioStorage): FxSettingsStore {
  const store = new Store(parseFx(readRaw(storage)))
  return {
    store,
    set(effects) {
      store.set(effects)
      try {
        storage?.setItem(FX_KEY, effects ? 'on' : 'off')
      } catch {
        // No storage: the change lasts for this page only.
      }
    },
  }
}

let page: FxSettingsStore | null = null

/** The page's setting (created on first use). */
export function fxSettings(): FxSettingsStore {
  page ??= createFxSettings(browserStorage())
  return page
}
